/* istanbul ignore file */

import URI from "urijs";

import { MapStringTo } from "../base-types";
import Guard from "../guard";
import { HttpMethod } from "../http-method";
import { IExtendedContentHubClient } from "./extended-client";
import { CancelCallback } from "./internal-client";
import { RequestMessage } from "./request-message";
import { ResponseMessage } from "./response-message";

export interface IRawClient {
    getAsync<T>(
        uri: string | URI,
        headers?: MapStringTo<string>,
        cancelCallback?: CancelCallback
    ): Promise<ResponseMessage<T>>;

    postAsync<T, K>(
        uri: string | URI,
        content?: T,
        headers?: MapStringTo<string>,
        cancelCallback?: CancelCallback
    ): Promise<ResponseMessage<K>>;

    putAsync<T, K>(
        uri: string | URI,
        content?: T,
        headers?: MapStringTo<string>,
        cancelCallback?: CancelCallback
    ): Promise<ResponseMessage<K>>;

    deleteAsync<T>(
        uri: string | URI,
        headers?: MapStringTo<string>,
        cancelCallback?: CancelCallback
    ): Promise<ResponseMessage<T>>;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sendAsync<T, K>(messageFactory: () => RequestMessage, cancelCallback?: CancelCallback): Promise<ResponseMessage<K>>;
}

export class RawClient implements IRawClient {
    private readonly _client: IExtendedContentHubClient;

    constructor(client: IExtendedContentHubClient) {
        Guard.notNullOrUndefined(client);
        this._client = client;
    }

    async getAsync<T = any>(
        uri: string | URI,
        headers?: MapStringTo<string>,
        cancelCallback?: CancelCallback
    ): Promise<ResponseMessage<T>> {
        return await this._client.internalClient.sendAsync<unknown, T>(
            () =>
                new RequestMessage<T>({
                    requestUri: uri,
                    method: HttpMethod.GET,
                    requestHeaders: headers,
                }),
            cancelCallback
        );
    }

    async postAsync<T = any, K = any>(
        uri: string | URI,
        content: unknown,
        headers?: MapStringTo<string>,
        cancelCallback?: CancelCallback
    ): Promise<ResponseMessage<K>> {
        if (!headers) {
            headers = {
                "Content-Type": "application/json",
            };
        }

        return await this._client.internalClient.sendAsync<T, K>(
            () =>
                new RequestMessage<T>({
                    requestUri: uri,
                    method: HttpMethod.POST,
                    requestHeaders: headers,
                    content: content,
                }),
            cancelCallback
        );
    }

    async putAsync<T = any, K = any>(
        uri: string | URI,
        content: unknown,
        headers?: MapStringTo<string>,
        cancelCallback?: CancelCallback
    ): Promise<ResponseMessage<K>> {
        if (!headers) {
            headers = {
                "Content-Type": "application/json",
            };
        }

        return await this._client.internalClient.sendAsync<T, K>(
            () =>
                new RequestMessage<T>({
                    requestUri: uri,
                    method: HttpMethod.PUT,
                    requestHeaders: headers,
                    content: content,
                }),
            cancelCallback
        );
    }

    async deleteAsync<T = any>(
        uri: string | URI,
        headers?: MapStringTo<string>,
        cancelCallback?: CancelCallback
    ): Promise<ResponseMessage<T>> {
        return await this._client.internalClient.sendAsync<unknown, T>(
            () =>
                new RequestMessage<T>({
                    requestUri: uri,
                    method: HttpMethod.DELETE,
                    requestHeaders: headers,
                }),
            cancelCallback
        );
    }

    async sendAsync<T = any, K = any>(
        messageFactory: () => RequestMessage<T>,
        cancelCallback?: CancelCallback
    ): Promise<ResponseMessage<K>> {
        Guard.notNullOrUndefined(messageFactory);

        return await this._client.internalClient.sendAsync<T, K>(messageFactory, cancelCallback);
    }
}
